import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Select,
  MenuItem,
  ListItemText,
  Grid,
  FormControl,
  InputLabel,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { i18n } from "../../translate/i18n";

// Utilitários para fontes de contato
import { getSourceIcon, getSourceColor, getSourceIconColor, isAdminSource } from "../../utils/contactSourceUtils";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
  },
  tagPreview: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    border: '1px solid',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  formControl: {
    marginBottom: theme.spacing(1),
    '& .MuiInputBase-root': {
      borderRadius: '8px',
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 12px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  selectMenu: {
    maxHeight: '200px',
    borderRadius: '8px',
    marginTop: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    overflowY: 'auto',
  },
  menuItem: {
    padding: '8px 16px',
    fontSize: '0.85rem',
    minHeight: '36px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(25, 118, 210, 0.12)',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.16)',
      },
    },
  },
  selectIcon: {
    fontSize: '18px',
    marginRight: '8px',
  },
}));

const CloseTicketModal = ({ modalOpen, onClose, ticket, tickets = [], action = "resolve" }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [selectedPhase, setSelectedPhase] = useState("");
  const [selectedContactSource, setSelectedContactSource] = useState("");
  const [contactSources, setContactSources] = useState([]);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requiredTagSetting, setRequiredTagSetting] = useState("disabled");
  const { get: getSetting } = useCompanySettings();
  
  // Se foi passado um array de tickets (bulk), usa ele, senão usa o ticket único
  const isMultipleTickets = tickets.length > 0;
  const ticketsToProcess = isMultipleTickets ? tickets : [ticket].filter(Boolean);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        fetchPhases();
        // Só carregar fontes de contato se não for bulk action
        if (!isMultipleTickets) {
          fetchContactSources();
        }
        
        // Carregar configuração de tag obrigatória
        const setting = await getSetting({ column: "requiredTag" });
        setRequiredTagSetting(setting.requiredTag || "disabled");
      } catch (err) {
        toast.error("Erro ao carregar configurações");
      }
    };

    if (modalOpen) {
      loadSettings();
    }
  }, [modalOpen, user.companyId]);

  const fetchContactSources = async () => {
    try {
      const response = await api.get("/contact-sources");
      // A API retorna { records, count, hasMore }, mas precisamos apenas do array records
      const contactSourcesList = response.data.records || response.data || [];
      setContactSources(Array.isArray(contactSourcesList) ? contactSourcesList.filter(source => source.isActive) : []);
    } catch (error) {
      console.error('Erro ao carregar fontes de contato:', error);
      toast.error("Erro ao carregar fontes de contato");
    }
  };

  const fetchPhases = async () => {
    try {
      const { data } = await api.get("tag/kanban");
      setPhases(data.lista);
    } catch (err) {
      toast.error("Erro ao carregar status Kanban");
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedPhase("");
    setSelectedContactSource("");
  };


  const getActionText = (type) => {
    const actions = {
      resolve: {
        button: isMultipleTickets ? `Resolver ${ticketsToProcess.length} ticket(s)` : "Resolver",
        title: isMultipleTickets ? `Resolver ${ticketsToProcess.length} ticket(s)` : "Resolver Ticket",
        success: isMultipleTickets ? `${ticketsToProcess.length} ticket(s) resolvido(s) com sucesso!` : "Ticket resolvido com sucesso!",
        error: "Erro ao resolver ticket(s)",
        select: isMultipleTickets ? 'fechar os tickets' : 'fechar a conversa'
      },
      archive: {
        button: isMultipleTickets ? `Arquivar ${ticketsToProcess.length} ticket(s)` : "Arquivar",
        title: isMultipleTickets ? `Arquivar ${ticketsToProcess.length} ticket(s)` : "Arquivar Ticket",
        success: isMultipleTickets ? `${ticketsToProcess.length} ticket(s) arquivado(s) com sucesso!` : "Ticket arquivado com sucesso!",
        error: "Erro ao arquivar ticket(s)",
        select: isMultipleTickets ? 'arquivar os tickets' : 'arquivar a conversa'
      }
    };
    return actions[action] || actions.resolve;
  };

  const handleConfirm = async () => {
    if (!selectedPhase) {
      toast.error(`Selecione um status antes de ${getActionText(action).select}`);
      return;
    }

    // Validação obrigatória da fonte de contato apenas quando há contatos sem fonte E não é bulk action
    if (!isMultipleTickets) {
      const hasContactsWithoutSource = ticketsToProcess.some(t => !t.contact?.contactSource?.id);
      if (hasContactsWithoutSource && !selectedContactSource) {
        toast.error("É obrigatório selecionar uma fonte de contato.");
        return;
      }
    }

    setLoading(true);
    try {
      
      // Processa todos os tickets
      const promises = ticketsToProcess.map(async (ticketItem) => {
        await api.delete(`/ticket-tags/${ticketItem.id}`);

        await api.put(`/ticket-tags/${ticketItem.id}/${selectedPhase}`, {
          tagId: selectedPhase,
          ticketId: ticketItem.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        // Define o status baseado na ação (resolve ou archive)
        await api.put(`/tickets/${ticketItem.id}`, {
          status: action === "resolve" ? "closed" : "archived",
          userId: user?.id || null,
          sendFarewellMessage: false,
          amountUsedBotQueues: 0,
        });

        // Atualizar fonte de contato apenas quando o contato não tem fonte definida E não é bulk action
        if (!isMultipleTickets && !ticketItem.contact?.contactSource?.id) {
          await api.put(`/contacts/${ticketItem.contact.id}/contactSource`, { 
            contactSourceId: selectedContactSource 
          });
        }

      });

      await Promise.all(promises);
      
      toast.success(getActionText(action).success);
      onClose(selectedPhase);
    } catch (err) {
      console.error(`Erro ao processar ticket(s): ${err}`);
      toast.error(getActionText(action).error);
    }
    setLoading(false);
  };

  const actionText = getActionText(action);
  const showTagSelection = false; // Tags são verificadas antes de abrir a modal

  // Verificar se algum contato não tem fonte de contato definida - apenas para tickets individuais
  const hasContactsWithoutSource = !isMultipleTickets && ticketsToProcess.some(t => !t.contact?.contactSource?.id);

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {actionText.title}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>


          <Grid item xs={12}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Etapa de Compra
            </Typography>
            <FormControl fullWidth variant="outlined" className={classes.formControl}>
              <InputLabel>Selecione uma etapa</InputLabel>
              <Select
                required
                value={selectedPhase}
                onChange={(e) => setSelectedPhase(e.target.value)}
                label="Selecione uma lane"
                MenuProps={{
                  classes: { list: classes.selectMenu },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                  },
                  getContentAnchorEl: null
                }}
              >
                {phases.map((phase) => (
                  <MenuItem key={phase.id} value={phase.id} className={classes.menuItem}>
                    <ListItemText primary={phase.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fonte do Contato - apenas para tickets individuais */}
          {!isMultipleTickets && hasContactsWithoutSource && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Fonte do Contato
              </Typography>
              <FormControl fullWidth variant="outlined" className={classes.formControl}>
                <InputLabel>Selecionar fonte do contato *</InputLabel>
                <Select
                  required
                  value={selectedContactSource}
                  onChange={(e) => setSelectedContactSource(e.target.value)}
                  label="Selecionar fonte do contato *"
                  MenuProps={{
                    classes: { list: classes.selectMenu },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }}
                >
                  <MenuItem value={''} className={classes.menuItem}>
                    <em>Selecione uma fonte (obrigatório)</em>
                  </MenuItem>
                  {contactSources.map((source) => (
                    <MenuItem key={source.id} value={source.id} className={classes.menuItem}>
                      <span style={{ 
                        display: "flex", 
                        alignItems: "center",
                        color: source.isFixed ? "#1976d2" : "inherit",
                        fontWeight: source.isFixed ? "bold" : "normal"
                      }}>
                        {source.isFixed && getSourceIcon(source.name) && (() => {
                          const sourceInfo = getSourceIcon(source.name);
                          const IconComponent = sourceInfo.icon;
                          
                          return (
                            <span style={{ marginRight: "8px" }}>
                              <IconComponent 
                                style={{ 
                                  fontSize: "18px", 
                                  color: isAdminSource(source.name) ? "#000000" : getSourceIconColor(source.name)
                                }} 
                              />
                            </span>
                          );
                        })()}
                        {source.name}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Preview da fonte selecionada */}
              {selectedContactSource && (() => {
                const selectedSource = contactSources.find(s => s.id === selectedContactSource);
                return selectedSource ? (
                  <div 
                    className={classes.tagPreview}
                    style={{ 
                      backgroundColor: "#e3f2fd",
                      borderColor: "#1976d2" 
                    }}
                  >
                    <Typography variant="body2">
                      Fonte do contato: <strong>{selectedSource.name}</strong>
                      {selectedSource.description && ` - ${selectedSource.description}`}
                    </Typography>
                  </div>
                ) : null;
              })()}
            </Grid>
          )}

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" variant="outlined" disabled={loading}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          loading={loading}
          disabled={!selectedPhase || (!isMultipleTickets && hasContactsWithoutSource && !selectedContactSource)}
        >
          {actionText.button}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default CloseTicketModal;
